import _ from 'lodash';
import React, { useCallback } from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { GiftFiltersInput, UserGender, UserSubscriptionTier } from '../../../../../__gqltypes__';
import continent from '../../../../../utils/dashboards/models/slicers/continent.json';
import countries from '../../../../../utils/dashboards/models/slicers/countries.json';
import defaultIsSpender from '../../../../../utils/dashboards/models/slicers/defaultIsSpender.json';
import defaultPlatform from '../../../../../utils/dashboards/models/slicers/defaultPlatform.json';

type Props = {
  onChange: (newFilters: GiftFiltersInput, sendDate: string | null) => void;
  onRefresh: () => void;
  sendDate: string | null;
  filters: GiftFiltersInput;
  recipientPreview?: React.ReactNode;
};

const FieldTooltip = ({
  tooltipId,
  tooltip,
}: {
  tooltipId: string;
  tooltip: string;
}): JSX.Element => (
  <OverlayTrigger placement="right" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
    <Button type="button" variant="link" className="p-0 ml-2 align-baseline">
      <i className="fas fa-info-circle text-muted" />
    </Button>
  </OverlayTrigger>
);

const FilterRow = ({
  label,
  tooltipId,
  tooltip,
  children,
}: {
  label: React.ReactNode;
  tooltipId?: string;
  tooltip?: string;
  children: React.ReactNode;
}): JSX.Element => (
  <Form.Group as={Row} className="mb-3">
    <Form.Label column md={3}>
      {label}
      {tooltip && tooltipId ? <FieldTooltip tooltipId={tooltipId} tooltip={tooltip} /> : null}
    </Form.Label>
    <Col md={6} className="align-self-end">
      {children}
    </Col>
  </Form.Group>
);

export const MassGiftFilters = (props: Props): JSX.Element => {
  const { onChange, onRefresh, sendDate, filters, recipientPreview } = props;

  const onReset = useCallback(() => {
    onChange({}, '');
  }, [onChange]);

  const onUpdateFilter = (
    value: string,
    key: string,
    processor: (val: string) => number | string | null | string[]
  ) => {
    const processedVal = processor(value);
    const newFilters = { ...filters, [key]: processedVal };
    onChange(newFilters, sendDate);
  };

  const stringProcessor = useCallback((val: string): string | null => {
    return val === 'All' || val === 'ALL' ? null : val;
  }, []);

  const numProcessor = useCallback((val: string): number | null => {
    const num = +val;
    return num <= 0 || Number.isNaN(num) ? null : num;
  }, []);

  const daysSinceLastOnlineProcessor = useCallback((val: string): number | null => {
    if (val.length === 0) {
      return null;
    }
    const num = +val;
    if (Number.isNaN(num)) {
      return null;
    }
    return num === 0 ? 30 : num;
  }, []);

  const arrProcessor = useCallback((val: string) => {
    return val.length > 0 ? val.split(',') : null;
  }, []);

  const genderOptions = Object.keys(UserGender).map((val) => ({
    text: val,
    value: val,
  }));
  const platformOptions = Object.keys(defaultPlatform.queries);
  const regionOptions = Object.keys(continent.queries);
  const countryOptions = countries.map(({ name, code }) => ({ text: name, value: code }));
  const isSpenderOptions = Object.keys(defaultIsSpender.queries);

  const spenderVal = _.isNil(filters.isSpender) ? 'All' : filters.isSpender > 0 ? 'spender' : 'non spender';
  const daysSinceLastOnlineValue = filters.daysSinceLastOnline === 30 ? '0' : filters.daysSinceLastOnline?.toString() ?? '';
  const isDaysSinceLastOnlineInvalid =
    !_.isNil(filters.daysSinceLastOnline) && (filters.daysSinceLastOnline < 0 || filters.daysSinceLastOnline > 90);
  const userIds = _.isNil(filters.specificUsers) ? '' : filters.specificUsers.join(',');
  return (
    <Card className="m-2 border border-secondary">
      <Card.Header className="d-flex align-items-center">
        <span className="font-weight-bold mr-2">Gift Targeting</span>
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="mass-gift-targeting-tooltip">
              Choose the recipients. Backend defaults are shown here: minimum app version 003.020.000 and last online
              within 30 days.
            </Tooltip>
          }
        >
          <i className="fas fa-info-circle text-muted" />
        </OverlayTrigger>
      </Card.Header>
      <Card.Body>
        <Form.Text muted className="mb-3">
          Search users with the filters below before scheduling the gift.
        </Form.Text>
        <Form
          key={JSON.stringify({
            country: filters.country,
            gender: filters.gender,
            isSpender: filters.isSpender,
            platform: filters.platform,
            region: filters.region,
            subTier: filters.subTier,
          })}
        >
          <FilterRow
            label="Send date"
            tooltipId="mass-gift-send-date-help"
            tooltip="Schedules when the gift is sent. Use a future date for pending gifts; past or current time may send immediately. Sent gifts cannot be edited or deleted."
          >
            <Form.Control type="datetime-local" value={sendDate ?? ''} onChange={(e) => onChange(filters, e.target.value || null)} />
          </FilterRow>
          <FilterRow
            label="App Version"
            tooltipId="mass-gift-app-version-help"
            tooltip="Minimum app version required for recipients. Example: enter 003.025.000 to include users on version 003.025.000 or higher. Empty or lower than 003.020.000 will use backend default 003.020.000."
          >
            <Form.Control
              type="text"
              value={filters.appVersion ?? ''}
              onChange={(e) => onUpdateFilter(e.target.value, 'appVersion', stringProcessor)}
            />
          </FilterRow>
          <FilterRow
            label="Platform"
            tooltipId="mass-gift-platform-help"
            tooltip="All means no platform restriction. Specific values filter users by their app install platform."
          >
            <Form.Control as="select" value={filters.platform ?? 'All'} onChange={(e) => onUpdateFilter(e.target.value, 'platform', stringProcessor)}>
              <option>All</option>
              {platformOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Control>
          </FilterRow>
          <FilterRow
            label="Region $"
            tooltipId="mass-gift-region-help"
            tooltip="This filter uses analytics data and can add BigQuery work to the Search User step."
          >
            <Form.Control as="select" value={filters.region ?? 'All'} onChange={(e) => onUpdateFilter(e.target.value, 'region', stringProcessor)}>
              <option>All</option>
              {regionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Control>
          </FilterRow>
          <FilterRow
            label="Country $"
            tooltipId="mass-gift-country-help"
            tooltip="This filter uses analytics data and can add BigQuery work to the Search User step."
          >
            <Form.Control as="select" value={filters.country ?? 'All'} onChange={(e) => onUpdateFilter(e.target.value, 'country', stringProcessor)}>
              <option>All</option>
              {countryOptions.map(({ text, value }) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Form.Control>
          </FilterRow>
          <FilterRow
            label="User Subscription Status:"
            tooltipId="mass-gift-subscription-help"
            tooltip="All means no subscription restriction. When a tier is selected, backend checks the user's current highest subscription tier."
          >
            <Form.Control as="select" value={filters.subTier ?? 'All'} onChange={(e) => onUpdateFilter(e.target.value, 'subTier', stringProcessor)}>
              <option>All</option>
              {Object.keys(UserSubscriptionTier).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Control>
          </FilterRow>
          <FilterRow
            label="Gender"
            tooltipId="mass-gift-gender-help"
            tooltip="All means no gender restriction."
          >
            <Form.Control as="select" value={filters.gender ?? 'All'} onChange={(e) => onUpdateFilter(e.target.value, 'gender', stringProcessor)}>
              <option>All</option>
              {genderOptions.map(({ text, value }) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Form.Control>
          </FilterRow>
          <FilterRow
            label="Age"
            tooltipId="mass-gift-age-help"
            tooltip="Empty means no age restriction. Values lower than or equal to zero are ignored."
          >
            <Form.Control type="number" value={filters.age?.toString() ?? ''} onChange={(e) => onUpdateFilter(e.target.value, 'age', numProcessor)} />
          </FilterRow>
          <FilterRow
            label="Days since last online"
          >
            <Form.Control
              type="number"
              value={daysSinceLastOnlineValue}
              isInvalid={isDaysSinceLastOnlineInvalid}
              onChange={(e) => onUpdateFilter(e.target.value, 'daysSinceLastOnline', daysSinceLastOnlineProcessor)}
            />
            <Form.Text muted style={{ opacity: 0.7 }}>
              Empty, 0, or negative uses the backend default of 30 days. Values above 90 are capped to 90.
            </Form.Text>
          </FilterRow>
          <FilterRow
            label="Days since first install"
            tooltipId="mass-gift-days-install-help"
            tooltip="Targets newly created users by first install date. Example: enter 7 to include users whose first install is within the last 7 days. Empty means this filter is not applied."
          >
            <Form.Control
              type="number"
              value={filters.daysSinceInstall?.toString() ?? ''}
              onChange={(e) => onUpdateFilter(e.target.value, 'daysSinceInstall', numProcessor)}
            />
          </FilterRow>
          <FilterRow
            label="Is spender $"
            tooltipId="mass-gift-spender-help"
            tooltip="This filter uses analytics data from paid purchase and subscription revenue events. Spender means users who have been flagged as paid spenders; non spender means users without that flag."
          >
            <Form.Control
              as="select"
              value={spenderVal}
              onChange={(e) =>
                onUpdateFilter(e.target.value, 'isSpender', (a) => (a === 'spender' ? 1 : a === 'non spender' ? 0 : null))
              }
            >
              <option>All</option>
              {isSpenderOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Control>
          </FilterRow>
          <FilterRow
            label={
              <>
                User Ids: <br />
                (separated by commas &quot;,&quot;)
              </>
            }
            tooltipId="mass-gift-user-ids-help"
            tooltip="Comma-separated user IDs. When filled, only these users are considered, but they must still match the other selected filters. Use no spaces between IDs."
          >
            <Form.Control as="textarea" value={userIds} onChange={(e) => onUpdateFilter(e.target.value, 'specificUsers', arrProcessor)} />
          </FilterRow>
          <Row className="justify-content-start ml-1">
            <Button onClick={onRefresh} style={{ marginRight: 10 }}>
              <i className="fas fa-search mr-2" />
              Search User
            </Button>
            <Button onClick={onReset}>
              <i className="fas fa-redo mr-2" />
              Reset
            </Button>
            <Col xs={12} className="px-0 mt-2">
              <Form.Text muted>
                Search User previews the recipient count and does not send the gift. Reset restores default filters
                and send date; selected rewards are kept.
              </Form.Text>
            </Col>
          </Row>
          {recipientPreview ? <div className="mt-3">{recipientPreview}</div> : null}
        </Form>
      </Card.Body>
    </Card>
  );
};
